import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { FileText, CheckCircle2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    legal_name: "",
    signatory_name: "",
    signatory_title: "",
    notice_address: "",
  });
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/public/agents/sign/${token}`);
        setData(d);
        setForm((f) => ({
          ...f,
          legal_name: d.invite_agency_name || "",
          signatory_name: d.invite_name || "",
        }));
        if (d.status === "signed" || d.status === "executed") setDone(true);
      } catch {
        setNotFound(true);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!sig) { toast.error("Please draw your signature"); return; }
    for (const k of ["effective_date", "legal_name", "signatory_name", "signatory_title", "notice_address"]) {
      if (!form[k] || !form[k].trim()) { toast.error("Please complete all fields"); return; }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/agents/sign/${token}`, { ...form, signature_data_url: sig });
      setDone(true);
      toast.success("Agreement signed. We'll be in touch shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <FullScreen>
        <Card className="max-w-lg w-full p-10 bg-white border border-[#E9ECEF] rounded-sm text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-red-600 font-bold">Link expired</div>
          <h1 className="mt-3 text-2xl font-black text-[#0B132B]">This agreement link isn't valid</h1>
          <p className="mt-3 text-sm text-[#6C757D]">Please contact your Advantage Warranty Group representative to have a new invitation sent.</p>
        </Card>
      </FullScreen>
    );
  }

  if (!data) {
    return <FullScreen><div className="text-sm text-[#6C757D]">Loading…</div></FullScreen>;
  }

  if (done) {
    return (
      <FullScreen>
        <Card className="max-w-lg w-full p-10 bg-white border border-[#E9ECEF] rounded-sm text-center">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Received</div>
          <h1 className="mt-2 text-2xl font-black text-[#0B132B]">
            {data.status === "executed" ? "Your agreement is fully executed" : "Thanks — we've received your signed agreement"}
          </h1>
          <p className="mt-3 text-sm text-[#6C757D] leading-relaxed">
            {data.status === "executed"
              ? "Check your email for a welcome message with your login link."
              : "Advantage Warranty Group will countersign shortly. You'll receive a welcome email with login instructions as soon as that's done."}
          </p>
        </Card>
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <div className="max-w-4xl w-full">
        <div className="mb-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Producer Agreement
          </div>
          <h1 className="mt-2 text-3xl font-black text-[#0B132B] tracking-tight">
            Welcome, {data.invite_name}
          </h1>
          <p className="mt-2 text-sm text-[#6C757D]">
            {data.invite_agency_name}
          </p>
        </div>

        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6 mb-6" data-testid="agent-sign-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] flex items-center gap-2">
              <FileText size={14} className="text-[#D4AF37]" /> The agreement
            </div>
            <a
              href={`${API}/public/agents/sign/${token}/agreement.pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-[#0B132B] hover:underline flex items-center gap-1"
              data-testid="agent-sign-download-pdf"
            >
              Open in new tab <ExternalLink size={12} />
            </a>
          </div>
          <iframe
            title="Producer Agreement"
            src={`${API}/public/agents/sign/${token}/agreement.pdf#toolbar=0&navpanes=0`}
            className="w-full h-[500px] border border-[#E9ECEF] rounded-sm bg-white"
          />
          {data.custom_addendum && (
            <div className="mt-6 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37] mb-2">Custom addendum</div>
              <div className="text-sm text-[#0B132B] whitespace-pre-wrap leading-relaxed">{data.custom_addendum}</div>
            </div>
          )}
        </Card>

        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-6">
            Fill and sign
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Effective date</Label>
              <Input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                data-testid="agent-sign-effective-date"
              />
            </div>
            <div>
              <Label>Legal name (business entity)</Label>
              <Input
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                data-testid="agent-sign-legal-name"
              />
            </div>
            <div>
              <Label>Signatory name</Label>
              <Input
                value={form.signatory_name}
                onChange={(e) => setForm({ ...form, signatory_name: e.target.value })}
                data-testid="agent-sign-signatory-name"
              />
            </div>
            <div>
              <Label>Signatory title</Label>
              <Input
                value={form.signatory_title}
                onChange={(e) => setForm({ ...form, signatory_title: e.target.value })}
                placeholder="e.g. Owner, Managing Partner"
                data-testid="agent-sign-signatory-title"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Notice address</Label>
              <Textarea
                rows={2}
                value={form.notice_address}
                onChange={(e) => setForm({ ...form, notice_address: e.target.value })}
                placeholder="Address where legal notices should be sent"
                data-testid="agent-sign-notice-address"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Signature</Label>
              <SignaturePad onChange={setSig} testId="agent-sign-signature-pad" />
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E9ECEF] flex items-center justify-between gap-4">
            <p className="text-xs text-[#6C757D] leading-relaxed">
              By clicking "Sign agreement", you acknowledge you have read the Producer Agreement above and agree to be bound by its terms.
            </p>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-11 px-6 whitespace-nowrap"
              data-testid="agent-sign-submit"
            >
              {submitting ? "Submitting…" : "Sign agreement"}
            </Button>
          </div>
        </Card>
      </div>
    </FullScreen>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      {children}
    </div>
  );
}
